import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from "@angular/core";

export interface TreeNode {
  key: string;
  type: string;
  name: string;
  components?: TreeNode[];
}

export interface FieldSelection {
  key: string;
  fullPath: string;
}

@Component({
  selector: "field-tree-picker",
  templateUrl: "./field-tree-picker.component.html",
  styleUrls: ["./field-tree-picker.component.scss"],
})
export class FieldTreePickerComponent implements OnChanges {
  @Input() public items: TreeNode[] = [];
  @Input() public selectedKey: string | null = null;
  @Input() public selectableTypes?: string[];
  @Input() public placeholder: string = "Search a field";
  @Input() public emptyMessage: string = "No fields available";

  @Output() public selectionChange = new EventEmitter<FieldSelection | null>();

  public searchTerm: string = "";
  public expandedKeys: Set<string> = new Set();
  public visibleTree: TreeNode[] = [];

  public ngOnChanges(_changes: SimpleChanges): void {
    this.recomputeVisibleTree();
    this.expandAncestorsOf(this.selectedKey);
  }

  public toggleExpand(node: TreeNode): void {
    if (this.expandedKeys.has(node.key)) {
      this.expandedKeys.delete(node.key);
    } else {
      this.expandedKeys.add(node.key);
    }
  }

  public applySearch(): void {
    this.recomputeVisibleTree();
  }

  private recomputeVisibleTree(): void {
    const pruned = this.pruneToSelectable(this.items || [], this.selectableTypes);
    const term = this.searchTerm.trim().toLowerCase();
    this.visibleTree = term ? this.filterByTerm(pruned, term) : pruned;
  }

  private filterByTerm(nodes: TreeNode[], term: string): TreeNode[] {
    const result: TreeNode[] = [];
    for (const n of nodes) {
      const children = this.filterByTerm(n.components || [], term);
      const selfMatches = (n.name || "").toLowerCase().includes(term);
      if (selfMatches || children.length > 0) {
        result.push({ ...n, components: children });
      }
    }
    return result;
  }

  private pruneToSelectable(nodes: TreeNode[], selectableTypes?: string[]): TreeNode[] {
    if (!selectableTypes) { return nodes; }
    const result: TreeNode[] = [];
    for (const n of nodes) {
      const children = this.pruneToSelectable(n.components || [], selectableTypes);
      const isSelf = selectableTypes.includes(n.type);
      if (isSelf || children.length > 0) {
        result.push({ ...n, components: children });
      }
    }
    return result;
  }

  public isSelectable(node: TreeNode): boolean {
    if (!this.selectableTypes) { return true; }
    return this.selectableTypes.includes(node.type);
  }

  public selectNode(node: TreeNode): void {
    if (!this.isSelectable(node)) { return; }
    const fullPath = this.getFullPath(node.key);
    if (fullPath === null) { return; }
    this.selectionChange.emit({ key: node.key, fullPath });
  }

  public getFullPath(key: string): string | null {
    return this.findPath(this.items || [], key, []);
  }

  private findPath(nodes: TreeNode[], key: string, ancestors: string[]): string | null {
    for (const n of nodes) {
      const trail = [...ancestors, n.name || n.key];
      if (n.key === key) { return trail.join(" › "); }
      if (n.components && n.components.length) {
        const found = this.findPath(n.components, key, trail);
        if (found) { return found; }
      }
    }
    return null;
  }

  private expandAncestorsOf(key: string | null): void {
    if (!key) { return; }
    const ancestors: string[] = [];
    this.collectAncestors(this.items || [], key, [], ancestors);
    for (const a of ancestors) { this.expandedKeys.add(a); }
  }

  private collectAncestors(nodes: TreeNode[], key: string, trail: string[], out: string[]): boolean {
    for (const n of nodes) {
      if (n.key === key) {
        for (const t of trail) { out.push(t); }
        return true;
      }
      if (n.components && n.components.length) {
        const found = this.collectAncestors(n.components, key, [...trail, n.key], out);
        if (found) { return true; }
      }
    }
    return false;
  }
}
